import posthog from "posthog-js";
import { createContext, PropsWithChildren, useContext, useState } from "react";

import { LOCAL_STORAGE } from "@/constants";
import { User } from "@/types/user";
import { setAuthCookie } from "@/utils/auth-cookie";
import { logoutClientSession } from "@/utils/auth-session";
import { getName } from "@/utils/getName";

export interface AuthContextType {
  login: (access_token: string, userData: User) => void;
  logout: () => void;
  user: User | null;
  userToken: string | null;
  updateUserState: (user: User) => void;
  updateAccessToken: (access_token: string) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getInitialAccessToken = (): string | null => localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN);

const getInitialUserData = (): User | null => {
  const localStorageUser = localStorage.getItem(LOCAL_STORAGE.USER);
  return localStorageUser ? (JSON.parse(localStorageUser) as User) : null;
};

const updateUser = (user: User) => {
  localStorage.setItem(LOCAL_STORAGE.USER, JSON.stringify(user));
};

const AuthProvider = (props: PropsWithChildren) => {
  const [accessToken, setAccessToken] = useState<string | null>(getInitialAccessToken());

  const [user, setUser] = useState<User | null>(getInitialUserData());

  const updateUserState = (userData: User) => {
    updateUser(userData);
    setUser(userData);
  };

  const updateAccessToken = (access_token: string) => {
    localStorage.setItem(LOCAL_STORAGE.ACCESS_TOKEN, access_token);
    setAuthCookie(access_token);
    setAccessToken(access_token);
  };

  const login = (access_token: string, userData: User) => {
    localStorage.setItem(LOCAL_STORAGE.ACCESS_TOKEN, access_token);
    setAuthCookie(access_token);
    localStorage.setItem(LOCAL_STORAGE.USER, JSON.stringify(userData));
    localStorage.removeItem(LOCAL_STORAGE.POSTHOG_PLAN_TYPE);
    const provider = localStorage.getItem("provider");
    if (provider) {
      localStorage.setItem("last_login_type", provider);
    }
    setAccessToken(access_token);
    const [firstName, lastName] = getName(userData.name || "");
    posthog.identify(userData.id.toString(), {
      email: userData.email,
      first_name: firstName,
      last_name: lastName,
      created_at: userData.created_at,
    });

    setUser(userData);
  };

  const logout = () => {
    logoutClientSession({ notifyServer: true });
    setAccessToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        user,
        userToken: accessToken,
        updateUserState,
        updateAccessToken,
      }}
      {...props}
    />
  );
};

const useAuth = () => useContext(AuthContext);

const useUserToken = () => {
  const auth = useAuth();
  return auth?.userToken ?? "";
};

export { AuthProvider, useAuth, useUserToken };
